import { ApiError } from '../client';
import { LockCodesSchema, type LockCodes } from '../schemas/lockCodes';
import type { LockSequenceType } from '../endpoints/confirmLockSequence';
import { deliveryDetailMockLockIds } from './deliveryDetail';

// The wire shape of the fixtures, before `LockCodesSchema` parses it. Named so the pointer override
// below can rewrite `current_position` without casting its way through `unknown`.
interface RawLockCodeRail {
  sequence: { code: string; position: number }[];
  current_position: number;
}
type RawLockCodes = Record<LockSequenceType, RawLockCodeRail | null>;

// The pregenerated tables of a cerradura are fixed-length, and the window wraps forward through the
// end of the table — these lengths are what make the wrap fixture below land on position 0.
const OPEN_TABLE_LENGTH = 400;
const RESET_TABLE_LENGTH = 200;

// Fictional codes only — never a real cerradura's sequence in a fixture, and never logged. Both
// lists carry a zero-leading code on purpose: the string modelling of the schema (and any future
// regression that turns codes into numbers) has to be exercised by the fixtures themselves.
const OPEN_CODES = [
  '2841',
  '0392',
  '7514',
  '1268',
  '9037',
  '4471',
  '5823',
  '6190',
  '0748',
  '3305',
  '8862',
  '2117',
  '9954',
  '6603',
  '1489',
  '7726',
];
const RESET_CODES = [
  '18497646',
  '02735118',
  '64820397',
  '39158204',
  '71062935',
  '05913827',
  '48276150',
  '93641078',
  '26085493',
  '80317264',
  '57429801',
  '14893675',
  '69204518',
  '31756042',
  '95138720',
  '42670983',
];

// Devices-service returns the window in ascending sequence order, wrapping forward through the end
// of the table, so positions are NOT necessarily monotonic — that wrap is a real case (CBX-10986)
// and a fixture has to carry it.
function buildRail(
  codes: readonly string[],
  firstPosition: number,
  tableLength: number,
  currentPosition: number,
): RawLockCodeRail {
  return {
    sequence: codes.map((code, i) => ({
      code,
      position: (firstPosition + i) % tableLength,
    })),
    current_position: currentPosition,
  };
}

// The everyday case: the full window on both carriles (10 previous · current · 5 next).
const FULL_WINDOW = {
  open: buildRail(OPEN_CODES, 127, OPEN_TABLE_LENGTH, 137),
  reset: buildRail(RESET_CODES, 2, RESET_TABLE_LENGTH, 12),
};

// The back edge: near position 0 the walk back stops instead of wrapping, so the window is short at
// the front and the UI's back control must come up disabled.
const BACK_EDGE = {
  open: buildRail(OPEN_CODES.slice(0, 9), 0, OPEN_TABLE_LENGTH, 3),
  reset: buildRail(RESET_CODES.slice(0, 6), 0, RESET_TABLE_LENGTH, 0),
};

// The forward wrap (positions 387…399, 0, 1, 2) beside a carril the CBX10 migration left with no
// codes at all — null, not an empty list. One fixture covers both edge shapes.
const WRAPPED_WITH_EMPTY_RESET = {
  open: buildRail(OPEN_CODES, 387, OPEN_TABLE_LENGTH, 397),
  reset: null,
};

const DATASET: Record<string, RawLockCodes> = {
  [deliveryDetailMockLockIds.stored]: FULL_WINDOW,
  [deliveryDetailMockLockIds.collected]: BACK_EDGE,
  [deliveryDetailMockLockIds.expired]: WRAPPED_WITH_EMPTY_RESET,
};

// The `retrieved` fixture always fails: the inline «No se han podido cargar los códigos» strip is a
// state agents will meet in prod (a cerradura devices does not know, devices down), so one showcase
// delivery must reach it without touching a toggle. `stored`/`storedExtended`/`blocked` share lock
// …0001 on purpose — the same physical cerradura has one sequence.
const FAILING_LOCK: string | null = deliveryDetailMockLockIds.retrieved;

// Dev/test hook: force the read to fail for any lock, to exercise the strip on a fixture that
// normally serves codes.
let forcedError = false;

export function setLockCodesMockError(fail: boolean): void {
  forcedError = fail;
}

// The WRITE twin of the read toggle: when set, the next confirmLockSequenceMock rejects with this
// backend code, so the generic banner is reachable without a backend. A code and not a boolean,
// matching the sibling writes — the panel maps none of them, but the shape stays the shape.
let injectedConfirmError: string | null = null;

export function setConfirmLockSequenceMockError(code: string | null): void {
  injectedConfirmError = code;
}

export function getConfirmLockSequenceMockError(): string | null {
  return injectedConfirmError;
}

// Pointer moves a confirm has recorded, applied on the way out. Kept beside the dataset rather than
// mutating it, so the fixtures stay immutable and undoing every move is one `clear()`. Keyed by lock
// AND carril: one cerradura, two tables that move independently.
const pointerMoves = new Map<string, number>();

function pointerKey(lockId: string, type: LockSequenceType): string {
  return `${lockId}:${type}`;
}

export function moveLockCodePointerInMock(
  lockId: string,
  type: LockSequenceType,
  newPosition: number,
): void {
  pointerMoves.set(pointerKey(lockId, type), newPosition);
}

// A null carril has no pointer to move: the migration left that table without codes, and inventing
// a rail here would paint a sequence the cerradura does not have.
function withPointerMoves(lockId: string, codes: RawLockCodes): RawLockCodes {
  const moved = (type: LockSequenceType): RawLockCodeRail | null => {
    const rail = codes[type];
    const position = pointerMoves.get(pointerKey(lockId, type));
    if (rail === null || position === undefined) return rail;
    return { ...rail, current_position: position };
  };
  return { open: moved('open'), reset: moved('reset') };
}

// One reset for every piece of shared state this resource holds — a second one would be a thing to
// forget, and a leaked pointer move reads as a real confirm in the next test.
export function resetLockCodesMock(): void {
  forcedError = false;
  injectedConfirmError = null;
  pointerMoves.clear();
}

export async function fetchLockCodesMock(lockId: string): Promise<LockCodes> {
  // Simulate network latency so the block's skeleton is actually visible in dev.
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (forcedError || lockId === FAILING_LOCK) {
    throw new ApiError(
      502,
      'lock_sequences_unavailable',
      { code: 'lock_sequences_unavailable' },
      'lock codes read failed',
    );
  }
  // An unlisted lock falls back to the full window: an unknown fixture should still show the block
  // working, the same fallback the box-log mock takes.
  return LockCodesSchema.parse(withPointerMoves(lockId, DATASET[lockId] ?? FULL_WINDOW));
}
